<template>
    <div class="step-questions">
        <v-row>
            <v-col cols="12" md="4">
                <v-card outlined class="step-card types-panel">
                    <v-card-title class="step-card__title">
                        {{ $t('surveys.wizard.questions.typesTitle') }}
                    </v-card-title>
                    <v-card-subtitle class="step-card__subtitle">
                        {{ $t('surveys.wizard.questions.typesSubtitle') }}
                    </v-card-subtitle>
                    <v-card-text>
                        <div
                            v-for="type in questionTypes"
                            :key="type.code"
                            class="type-item"
                            :class="{ 'type-item--disabled': readonly || hasPendingQuestion }"
                            :title="$t(`surveys.questionTypes.${type.code}.description`)"
                            @click="!readonly && !hasPendingQuestion && addQuestion(type)"
                        >
                            <v-icon small class="mr-2">{{ getTypeIcon(type.code) }}</v-icon>
                            {{ $t(`surveys.questionTypes.${type.code}.name`) }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="8" class="questions-col">
                <div v-if="localQuestions.length === 0" class="empty-state">
                    <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
                    <p class="mt-3">{{ $t('surveys.wizard.questions.empty') }}</p>
                </div>

                <div
                    v-for="(question, index) in localQuestions"
                    :key="index"
                    class="question-card"
                >
                    <div class="question-card__header">
                        <span class="question-card__number">
                            {{ $t('surveys.wizard.questions.questionLabel') }} #{{ index + 1 }}
                        </span>
                        <v-chip x-small :color="getTypeBadgeColor(question.type)">
                            {{ getTypeName(question.type) }}
                        </v-chip>
                    </div>

                    <v-text-field
                        v-model="question.title"
                        :placeholder="$t('surveys.wizard.questions.questionPlaceholder')"
                        outlined
                        dense
                        class="question-card__input"
                        :readonly="readonly"
                        :rules="[rules.required]"
                        @input="emitUpdate"
                    ></v-text-field>

                    <!-- Options for selection types -->
                    <div
                        v-if="supportsOptions(question.type)"
                        class="question-card__options"
                    >
                        <div
                            v-for="(option, optIndex) in question.options"
                            :key="optIndex"
                            class="question-card__option"
                        >
                            <v-icon small class="mr-2" color="grey">
                                {{ question.type === 'SINGLE_CHOICE' ? 'mdi-radiobox-blank' : 'mdi-checkbox-blank-outline' }}
                            </v-icon>
                            <v-text-field
                                v-model="option.label"
                                dense
                                outlined
                                hide-details
                                class="option-input"
                                :readonly="readonly"
                                @input="emitUpdate"
                            ></v-text-field>
                            <v-btn
                                v-if="!readonly"
                                icon
                                x-small
                                @click="removeOption(index, optIndex)"
                            >
                                <v-icon small color="red">mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <v-btn
                            v-if="!readonly"
                            text
                            x-small
                            color="primary"
                            class="mt-2"
                            @click="addOption(index)"
                        >
                            <v-icon x-small left>mdi-plus</v-icon>
                            {{ $t('surveys.wizard.questions.addOption') }}
                        </v-btn>
                    </div>

                    <!-- Config for question types with configSchema -->
                    <div
                        v-if="getConfigFields(question.type).length"
                        class="question-card__config"
                    >
                        <div
                            v-for="field in getConfigFields(question.type)"
                            :key="field.key"
                            class="config-field"
                        >
                            <v-select
                                v-if="field.options"
                                v-model="question.config[field.key]"
                                :label="field.label"
                                :items="field.options"
                                dense
                                outlined
                                hide-details
                                :readonly="readonly"
                                @change="emitUpdate"
                            ></v-select>
                            <v-checkbox
                                v-else-if="field.type === 'boolean'"
                                v-model="question.config[field.key]"
                                :label="field.label"
                                dense
                                hide-details
                                color="primary"
                                :readonly="readonly"
                                @change="emitUpdate"
                            ></v-checkbox>
                            <v-text-field
                                v-else
                                v-model="question.config[field.key]"
                                :label="field.label"
                                :type="field.type === 'number' ? 'number' : 'text'"
                                dense
                                outlined
                                hide-details
                                :readonly="readonly"
                                @input="emitUpdate"
                            ></v-text-field>
                        </div>
                    </div>

                    <div class="question-card__footer">
                        <v-checkbox
                            v-model="question.required"
                            :label="$t('surveys.wizard.questions.required')"
                            dense
                            hide-details
                            color="primary"
                            :readonly="readonly"
                            @change="emitUpdate"
                        ></v-checkbox>
                        <v-btn
                            v-if="!readonly"
                            text
                            x-small
                            color="red"
                            @click="removeQuestion(index)"
                        >
                            <v-icon small left>mdi-close</v-icon>
                            {{ $t('surveys.wizard.questions.delete') }}
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    name: "StepQuestions",
    props: {
        questions: {
            type: Array,
            required: true
        },
        questionTypes: {
            type: Array,
            required: true
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            localQuestions: [...this.questions],
            rules: {
                required: value => !!value || this.$t('surveys.wizard.generalData.required')
            }
        };
    },

    watch: {
        questions: {
            handler(newVal) {
                this.localQuestions = [...newVal];
            },
            deep: true
        },
        localQuestions: {
            handler() {
                this.emitValidity();
            },
            deep: true
        }
    },

    mounted() {
        this.emitValidity();
    },

    computed: {
        hasPendingQuestion() {
            return this.localQuestions.some(q => !this.isQuestionComplete(q));
        }
    },

    methods: {
        supportsOptions(typeCode) {
            return ['SINGLE_CHOICE', 'MULTI_CHOICE', 'DROPDOWN'].includes(typeCode);
        },

        getQuestionType(typeCode) {
            return this.questionTypes.find(t => t.code === typeCode) || {};
        },

        getConfigFields(typeCode) {
            const type = this.getQuestionType(typeCode);
            if (!type.configSchema) return [];
            return Object.entries(type.configSchema)
                .filter(([key]) => !(typeCode === 'RATING' && key === 'scale'))
                .map(([key, schema]) => ({
                    key,
                    label: this.getConfigLabel(key),
                    type: schema.type,
                    options: schema.options
                }));
        },

        getConfigLabel(key) {
            const translated = this.$t('surveys.wizard.questions.configLabels.' + key);
            return translated !== 'surveys.wizard.questions.configLabels.' + key ? translated : key;
        },

        isQuestionComplete(q) {
            if (!q.title || !q.title.trim()) return false;
            if (this.supportsOptions(q.type)) {
                if (!q.options || q.options.length < 2) return false;
                if (q.options.some(o => !o.label || !o.label.trim())) return false;
            }
            const configFields = this.getConfigFields(q.type);
            for (const field of configFields) {
                const value = (q.config || {})[field.key];
                if (value === '' || value === null || value === undefined) return false;
            }
            return true;
        },

        addQuestion(type) {
            const config = {};
            if (type.configSchema) {
                Object.entries(type.configSchema).forEach(([key, schema]) => {
                    if (schema.default !== undefined) {
                        config[key] = schema.default;
                    } else if (schema.type === 'boolean') {
                        config[key] = false;
                    } else if (schema.type === 'number') {
                        config[key] = null;
                    } else {
                        config[key] = '';
                    }
                });
            }

            if (type.code === 'RATING') {
                config.scale = 5;
            }

            const newQuestion = {
                type: type.code,
                title: "",
                required: true,
                config,
                options: type.supportsOptions ? [{ label: "" }, { label: "" }] : []
            };
            this.localQuestions.push(newQuestion);
            this.emitUpdate();
        },

        removeQuestion(index) {
            this.localQuestions.splice(index, 1);
            this.emitUpdate();
        },

        addOption(questionIndex) {
            this.localQuestions[questionIndex].options.push({ label: "" });
            this.emitUpdate();
        },

        removeOption(questionIndex, optionIndex) {
            this.localQuestions[questionIndex].options.splice(optionIndex, 1);
            this.emitUpdate();
        },

        emitUpdate() {
            this.$emit('update', [...this.localQuestions]);
        },

        emitValidity() {
            this.$emit('valid', !this.hasPendingQuestion);
        },

        getTypeIcon(typeCode) {
            const icons = {
                'SHORT_TEXT': 'mdi-text-short',
                'LONG_TEXT': 'mdi-text-long',
                'TRUE_FALSE': 'mdi-toggle-switch-outline',
                'SINGLE_CHOICE': 'mdi-radiobox-marked',
                'MULTI_CHOICE': 'mdi-checkbox-marked',
                'DROPDOWN': 'mdi-menu-down',
                'RATING': 'mdi-star',
                'LINEAR_SCALE': 'mdi-trending-up',
                'NUMBER': 'mdi-numeric',
                'DATE': 'mdi-calendar',
                'NPS': 'mdi-poll'
            };
            return icons[typeCode] || 'mdi-help-circle';
        },

        getTypeName(typeCode) {
            return this.$t(`surveys.questionTypes.${typeCode}.name`);
        },

        getTypeBadgeColor(typeCode) {
            const colors = {
                'SHORT_TEXT': 'grey lighten-3',
                'LONG_TEXT': 'grey lighten-3',
                'TRUE_FALSE': 'grey lighten-3',
                'SINGLE_CHOICE': 'purple lighten-5',
                'MULTI_CHOICE': 'blue lighten-5',
                'DROPDOWN': 'teal lighten-5',
                'RATING': 'amber lighten-5',
                'LINEAR_SCALE': 'indigo lighten-5',
                'NUMBER': 'orange lighten-5',
                'DATE': 'pink lighten-5',
                'NPS': 'cyan lighten-5'
            };
            return colors[typeCode] || 'grey lighten-3';
        }
    }
};
</script>

<style lang="css" scoped>
    .step-questions {
        width: 100%;
    }

    .step-card {
        border-radius: 16px !important;
        position: sticky;
        top: 20px;
        width: 100%;
    }

    .step-card__title {
        font-size: 14px !important;
        font-weight: 700 !important;
        color: var(--v-primary-base);
    }

    .step-card__subtitle {
        font-size: 12px !important;
        color: #9A9AA6;
    }

    .type-item {
        display: flex;
        align-items: center;
        padding: 12px;
        border: 1px solid #ECEAF2;
        border-radius: 10px;
        font-size: 13px;
        font-weight: 600;
        color: #1F1F2B;
        cursor: pointer;
        margin-bottom: 10px;
        transition: all 0.2s;
    }

    .type-item:hover {
        border-color: #6E17A5;
        background: #FAF9FC;
    }

    .type-item--disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .type-item--disabled:hover {
        border-color: #ECEAF2;
        background: white;
    }

    .types-panel {
        position: relative;
        top: 0;
        max-height: calc(100vh - 260px);
        overflow-y: auto;
    }

    .questions-col {
        max-height: calc(100vh - 260px);
        overflow-y: auto;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        color: #9A9AA6;
        font-size: 14px;
    }

    .question-card {
        background: white;
        border: 1px solid #ECEAF2;
        border-radius: 12px;
        padding: 16px;
        margin-bottom: 14px;
    }

    .question-card__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
    }

    .question-card__number {
        font-size: 12px;
        font-weight: 700;
        color: #9A9AA6;
    }

    .question-card__input {
        margin-bottom: 8px;
    }

    .question-card__options {
        padding-left: 10px;
        margin-bottom: 12px;
    }

    .question-card__option {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
    }

    .option-input {
        flex: 1;
    }

    .question-card__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #ECEAF2;
        padding-top: 10px;
        margin-top: 4px;
    }

    .question-card__config {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding: 12px;
        background: #FAF9FC;
        border-radius: 8px;
        margin-bottom: 12px;
    }

    .config-field {
        flex: 1 1 160px;
        min-width: 120px;
    }
</style>
